import { Component, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import Logo from 'dibs-logo/exports/Logo';
import { Link } from 'dibs-elements/exports/Link';
import { openLoginModal } from '../actions/checkoutAuthActions';
import { isLoggedIn } from '../helpers/checkoutUser';
import CheckoutTitle from './CheckoutTitle';
import CheckoutContact from './CheckoutContact';
import CheckoutHeaderBanner from './CheckoutHeaderBanner';
import AbandonCheckoutModal from '../shared/components/AbandonCheckoutModal';
import { isCheckoutCancelationPolicyVariant } from '../helpers/checkoutCancelationPolicyTestHelpers';
import cn from 'classnames';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import styles from './styles/CheckoutHeader.scss';

const CheckoutCancelationPolicy = lazy(() =>
    import(/* webpackChunkName: "CheckoutCancelationPolicy" */ './CheckoutCancelationPolicy')
);

class CheckoutPageHeader extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isModalOpen: false,
        };
        this.onLogin = this.onLogin.bind(this);
        this.onClickLogo = this.onClickLogo.bind(this);
    }
    componentDidUpdate(prevProps) {
        if (this.props.buyer !== prevProps.buyer && this.props.buyer && this.props.buyer.isTrade) {
            this.props.dispatch({ type: 'HAS_TRADE_STATUS' });
        }
    }
    onLogin(event) {
        const { dispatch } = this.props;
        event.preventDefault();
        openLoginModal({ dispatch });
    }
    onClickLogo(event) {
        event.preventDefault();
        this.setState({ isModalOpen: true });
    }
    render() {
        const { viewer, buyer } = this.props;
        const { item } = viewer || {};
        const isCancelationPolicyVariant = isCheckoutCancelationPolicyVariant();

        // hide log in link if you are logged in, or if you have completed the
        // address step using guest checkout
        const showLoginLink = !(isLoggedIn() || buyer);
        const isGetHelp = this.props.checkoutHelpType === 'GET_HELP';
        return (
            <div id="checkout-header" className={styles.wrapper}>
                <div className={`${styles.header} cf primary-page-width`}>
                    <div className={styles.leftContent}>
                        <Link
                            href="/"
                            className={styles.logoLink}
                            dataTn="checkout-page-header-logo"
                            title="1stDibs.com"
                            onClick={this.onClickLogo}
                        >
                            <Logo className={styles.logo} />
                        </Link>
                        <div className={styles.labelContainer}>
                            <span
                                className={cn(styles.label, {
                                    [styles.labelVariant]: isCancelationPolicyVariant,
                                })}
                                data-tn="checkout-page-header-label"
                            >
                                <CheckoutTitle />
                            </span>
                            {isCancelationPolicyVariant && (
                                <span className={styles.contactContainer}>
                                    <CheckoutContact />
                                    {showLoginLink && (
                                        <Link
                                            href=""
                                            className={cn(styles.login, {
                                                [styles.loginNoHelp]: !isGetHelp,
                                            })}
                                            onClick={this.onLogin}
                                            dataTn="checkout-login"
                                        >
                                            <FormattedMessage
                                                id="checkout.PageHeader.logIn"
                                                defaultMessage="Log In"
                                            />
                                        </Link>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={styles.rightContent}>
                        {isCancelationPolicyVariant ? (
                            <Suspense fallback={null}>
                                <CheckoutCancelationPolicy item={item} />
                            </Suspense>
                        ) : (
                            <>
                                <CheckoutContact />
                                {showLoginLink && (
                                    <Link
                                        href=""
                                        className={cn(styles.login, {
                                            [styles.loginNoHelp]: !isGetHelp,
                                        })}
                                        onClick={this.onLogin}
                                        dataTn="checkout-login"
                                    >
                                        <FormattedMessage
                                            id="checkout.PageHeader.logIn"
                                            defaultMessage="Log In"
                                        />
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <CheckoutHeaderBanner {...{ viewer }} />
                <AbandonCheckoutModal
                    isOpen={this.state.isModalOpen}
                    onClose={() => this.setState({ isModalOpen: false })}
                    onReturnToItem={() => {
                        if (item?.localizedPdpUrl) {
                            window.location.assign(
                                handleLocaleUrl(item.localizedPdpUrl, GLOBAL_CLIENT_ONLY_LOCALE)
                            );
                        } else {
                            this.setState({ isModalOpen: false });
                        }
                    }}
                    item={item}
                />
            </div>
        );
    }
}

CheckoutPageHeader.propTypes = {
    buyer: PropTypes.object,
    userRelationships: PropTypes.array,
    viewer: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    tradeStatus: PropTypes.object,
    checkoutHelpType: PropTypes.string,
    banner: PropTypes.object,
};

const ConnectedCheckoutPageHeader = connect(({ tradeStatus, ui }) => ({
    tradeStatus,
    checkoutHelpType: ui.checkoutHelpType,
}))(CheckoutPageHeader);

export { CheckoutPageHeader };

export default createFragmentContainer(ConnectedCheckoutPageHeader, {
    viewer: graphql`
        fragment CheckoutPageHeader_viewer on Viewer {
            item(itemId: $itemId, pageType: CHECKOUT) {
                ...CheckoutCancelationPolicy_item
                localizedPdpUrl
            }
            ...CheckoutHeaderBanner_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutPageHeader_buyer on User {
            isTrade: isVerifiedTrade
        }
    `,
});
